var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { css } from '@emotion/react';
import { cssVariableWithOverride, fontOverrides } from '../overrides';
import { darkThemeStyle, theme } from '../theme';
import { tokens } from '../tokens';
var sizeMap = {
    large: {
        fontSize: tokens.fontSizes.large,
        sizing: tokens.sizing.large,
        spacing: tokens.spacing.large,
    },
    medium: {
        fontSize: cssVariableWithOverride(fontOverrides.buttonMedium, tokens.fontSizes.medium),
        sizing: tokens.sizing.medium,
        spacing: tokens.spacing.medium,
    },
    small: {
        fontSize: tokens.fontSizes.small,
        sizing: tokens.sizing.small,
        spacing: tokens.spacing.small,
    },
};
export function getThemedStyle(_a) {
    var variant = _a.variant;
    switch (variant) {
        case 'primary':
            return {
                backgroundColor: theme.brand.main,
                color: theme.brand.textOnColor,
                hoverBackgroundColor: theme.background.hoverStrong,
            };
        case 'plain':
            return {
                backgroundColor: 'transparent',
                color: theme.text.link,
                hoverBackgroundColor: theme.background.hover,
            };
        case 'destructive':
            return {
                backgroundColor: theme.error.main,
                color: theme.error.textOnColor,
                hoverBackgroundColor: theme.background.hoverStrong,
            };
        case 'upgrade':
            return {
                backgroundColor: theme.upgrade.main,
                color: theme.upgrade.textOnColor,
                hoverBackgroundColor: theme.background.hoverStrong,
            };
        case 'secondary':
        default:
            return {
                backgroundColor: 'transparent',
                borderColor: theme.border.interactive,
                color: theme.text.main,
                hoverBackgroundColor: theme.background.hover,
            };
    }
}
var buttonLoadingStaticStyle = css({
    // Hide all direct descendants, except the wrapper for the loader
    '& > *:not(:last-child)': {
        height: 0,
        opacity: 0,
    },
});
var buttonStaticStyle = css({
    '::before': {
        borderRadius: '2px',
        content: '""',
        display: 'block',
        height: '100%',
        inset: 0,
        position: 'absolute',
        width: '100%',
        zIndex: 0,
    },
    alignItems: 'center',
    borderColor: 'transparent',
    borderRadius: tokens.borderRadius.medium,
    borderStyle: 'solid',
    borderWidth: tokens.borderWidth.medium,
    cursor: 'pointer',
    display: 'inline-flex',
    flexDirection: 'column',
    flexShrink: 0,
    fontFamily: tokens.fontFamilies.sansSerif,
    fontWeight: tokens.fontWeights.bold,
    justifyContent: 'center',
    lineHeight: tokens.lineHeights.tight,
    margin: 0,
    outline: 0,
    padding: 0,
    position: 'relative',
    textDecoration: 'none',
    transition: 'background-color 125ms ease-out',
    userSelect: 'none',
    verticalAlign: 'middle',
});
// Generate button element style based on various options
export function buttonStyle(_a) {
    var fullWidth = _a.fullWidth, inverted = _a.inverted, isFocusVisible = _a.isFocusVisible, isIconOnly = _a.isIconOnly, isLoading = _a.isLoading, size = _a.size, themedStyle = _a.themedStyle;
    var hasBorder = themedStyle.borderColor != null;
    return __spreadArray(__spreadArray(__spreadArray([], __read((inverted ? [darkThemeStyle] : [])), false), [
        buttonStaticStyle,
        css(__assign(__assign(__assign(__assign({ backgroundColor: themedStyle.backgroundColor, color: themedStyle.color, fontSize: sizeMap[size].fontSize, height: sizeMap[size].sizing, minWidth: sizeMap[size].sizing, width: fullWidth ? '100%' : 'auto' }, (!isIconOnly && {
            paddingLeft: sizeMap[size].spacing,
            paddingRight: sizeMap[size].spacing,
        })), (hasBorder && {
            '::before': {
                borderRadius: '2px',
                margin: 0,
            },
            borderColor: themedStyle.borderColor,
        })), { ':active': {
                backgroundColor: themedStyle.backgroundColor,
            }, ':disabled': {
                cursor: 'default',
                opacity: tokens.opacity.high,
                pointerEvents: 'none',
            }, ':hover': __assign(__assign({}, (!hasBorder && {
                borderColor: themedStyle.hoverBackgroundColor,
            })), { '::before': {
                    backgroundColor: themedStyle.hoverBackgroundColor,
                } }), '> *': {
                zIndex: tokens.zIndex.default,
            } }), (isFocusVisible && {
            outline: "2px solid ".concat(theme.border.focusOutline),
            outlineOffset: 2,
        })))
    ], false), __read((isLoading ? [buttonLoadingStaticStyle] : [])), false);
}
var loaderWrapperStaticStyle = css({
    // Override for child opacity from parent
    '&&': {
        opacity: 1,
    },
    alignItems: 'center',
    display: 'flex',
});
export function loaderWrapperStyle(_a) {
    var hasLoadingLabel = _a.hasLoadingLabel, size = _a.size;
    return __spreadArray([
        loaderWrapperStaticStyle
    ], __read((hasLoadingLabel
        ? [
            css({
                span: {
                    paddingLeft: size === 'small' ? tokens.spacing.xsmall : tokens.spacing.small,
                },
            }),
        ]
        : [])), false);
}
export function loaderStyle(_a) {
    var themedStyle = _a.themedStyle;
    return css({
        stroke: themedStyle.color,
    });
}
var innerContentStaticStyle = css({
    alignItems: 'center',
    display: 'flex',
    height: '100%',
});
export function innerContentStyle(_a) {
    var size = _a.size;
    return [
        innerContentStaticStyle,
        css({
            gap: size === 'small' ? tokens.spacing.xsmall : tokens.spacing.small,
        }),
    ];
}
